import { BoundTextField, BoundTextFieldProps, Only, TextFieldXss, useComputed } from "@homebound/beam";
import { ObjectState } from "src/utils/formState";
import { AiProposalValue } from "./AiProposalValue";
import { AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import {
  type AiProposalParser,
  parseAiString,
  pendingProposedValue,
  resolveAiProposalStatus,
} from "./utils/bindAiProposal";

// Derive proposedValue from aiProposal so callers cannot supply a conflicting value.
export type BoundAiTextFieldProps<X> = Omit<BoundTextFieldProps<X>, "proposedValue"> & {
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
  /** Converts the proposal to the field's text, e.g. a catalog fallback or formatted square footage. */
  parser?: AiProposalParser<string>;
};

/** Binds a text field to its saved value and parsed AI proposal. */
export function BoundAiTextField<X extends Only<TextFieldXss, X>>(props: BoundAiTextFieldProps<X>) {
  const { field, aiProposal, onChange, parser = parseAiString, ...others } = props;
  // BoundTextField tracks its own reads, but pendingProposedValue runs here and needs its own reactive scope.
  const proposedValue = useComputed(() => pendingProposedValue(aiProposal, parser), [aiProposal, parser]);
  const originalValue = useComputed(() => field.originalValue, [field]);
  return (
    <AiProposalValue aiProposal={aiProposal} originalValue={originalValue}>
      <BoundTextField
        field={field}
        proposedValue={proposedValue}
        onChange={(value) => {
          field.set(value);
          resolveAiProposalStatus(aiProposal, parser, value);
          onChange?.(value);
        }}
        {...others}
      />
    </AiProposalValue>
  );
}
